import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { PaymentMethod } from '@stripe/stripe-js';
import styles from './account-extras.module.scss';
import { IAppState } from '../../../../store';
import ExtrasStep from '../../../signup/steps/extras/extras-step';
import { Product } from '../../../deprecated/payment/types';
import paymentAPI from '../../../deprecated/payment/api';
import Loader from '../../../../components/content/loader';
import { useAppDispatch } from 'src/store/hooks';
import { setAddOnProducts } from 'src/store/payment';
import { useSubscription } from 'src/hooks/useSubscription';
import { isUserFromNewZealand, isUserFromUK } from 'src/lib/utils';
import { useShopifyVariants } from 'src/hooks/useShopifyVariants';

const AccountExtras = (): JSX.Element => {
  const dispatch = useAppDispatch();

  // Fetch shopify variants into Redux (used by ProteinFlavorField and SupplementPackageCard)
  useShopifyVariants();

  const user = useSelector((state: IAppState) => state.auth?.user);
  const { isSubscriptionLoading, refetchSubscriptions } = useSubscription(user);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [products, setProducts] = useState<Product[] | undefined>();
  const [regetProducts, setRegetProducts] = useState<boolean>(false);
  const hasRefetchedRef = useRef(false);

  useEffect(() => {
    (async (): Promise<void> => {
      try {
        if (user?.payment_provider === 'Stripe') {
          const response = await paymentAPI.get('stripe/customer-my-account', {
            params: { email: user?.email },
          });

          const defaultPaymentMethod: PaymentMethod =
            response?.data?.customer.invoice_settings.default_payment_method;

          if (defaultPaymentMethod) {
            setPaymentMethod(defaultPaymentMethod);
          }
        }

        // Get Products Data
        const { data: productsData } = await paymentAPI.get(
          '/user/getClientConsumableProducts',
          {
            params: {
              currency: isUserFromUK()
                ? 'GBP'
                : isUserFromNewZealand()
                ? 'NZD'
                : 'AUD',
              challenge: true,
            },
          }
        );
        setProducts(productsData);
        dispatch(setAddOnProducts(productsData));
      } catch (e) {
        console.log('getProducts error', e);
      }

      setIsLoading(false);
      setRegetProducts(false);
    })();
  }, [user, regetProducts, dispatch]);

  // Refetch subscriptions when products are being re-fetched
  useEffect(() => {
    if (regetProducts && !hasRefetchedRef.current) {
      hasRefetchedRef.current = true;
      refetchSubscriptions();
    } else if (!regetProducts) {
      hasRefetchedRef.current = false; // Reset for next cycle
    }
  }, [regetProducts, refetchSubscriptions]);

  return (
    <div className={styles.accountExtras}>
      <h3>Add-ons</h3>
      {!user || isLoading || regetProducts || isSubscriptionLoading ? (
        <Loader />
      ) : (
        <ExtrasStep
          paymentMethod={paymentMethod}
          products={products}
          setRegetProducts={setRegetProducts}
        />
      )}
    </div>
  );
};

export default AccountExtras;
